// License Management account screen (modules/licenses/templates/account.php + license.php).
//
// MARKUP CONTRACT
//   <section data-son-license="{key}">
//     <button data-son-license-toggle data-show data-hide>      show / hide inactive devices
//     <button data-son-license-action="revoke|reactivate" data-device="{device id}">
//     <button data-son-license-popup="{popup name}">            info popup only, no call
//     <button data-son-license-ilok>                            opens the iLok form
//     <p data-son-license-error hidden>
//   <dialog data-son-licenses-ilok>                              the iLok form, once per screen
//   <form data-son-licenses-register>                            register a key bought elsewhere
//     <p data-son-licenses-register-message hidden>
//
// Every action answers the license rendered again; the block replaces itself, so the
// listeners are delegated from the document. Texts come from sonLicenses (Account::script_data).

import { infoDialog } from './dialog';

const config = () => window.sonLicenses;

function popup(name) {
    const { title, text } = config().popups[name] || {};

    return infoDialog({ title, text, button: config().button });
}

async function post(action, data) {
    const response = await fetch(config().endpoint + action, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': config().nonce },
        body: JSON.stringify(data),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(json.message || config().error);
    }

    return json;
}

function replace(key, html) {
    const block = document.querySelector(`[data-son-license="${CSS.escape(key)}"]`);
    if (block && html) {
        block.outerHTML = html;
    }
}

// The button that had focus went with the old block; keyboard users continue at the new one
function focusLicense(key) {
    const block = document.querySelector(`[data-son-license="${CSS.escape(key)}"]`);
    if (block) {
        block.tabIndex = -1;
        block.focus();
    }
}

function showError(element, message) {
    if (element) {
        element.textContent = message;
        element.hidden = !message;
    }
}

// While one call runs, every other action on the screen is disabled, visibly
const ACTIONS = '[data-son-license-action], [data-son-license-ilok], [data-son-licenses-register] [type=submit]';
let busy = false;

function setBusy(on) {
    busy = on;
    document.querySelectorAll(ACTIONS).forEach((button) => {
        if (on && !button.disabled) {
            button.disabled = true;
            button.dataset.sonBusy = '';
        } else if (!on && 'sonBusy' in button.dataset) {
            button.disabled = false;
            delete button.dataset.sonBusy;
        }
    });
}

async function act(button) {
    const block = button.closest('[data-son-license]');
    if (busy || !block) {
        return;
    }
    setBusy(true);
    showError(block.querySelector('[data-son-license-error]'), '');

    try {
        const key = block.dataset.sonLicense;
        const result = await post(button.dataset.sonLicenseAction, { key, device_id: Number(button.dataset.device) });
        replace(key, result.html);
        popup(result.popup).then(() => focusLicense(key));
    } catch (error) {
        showError(block.querySelector('[data-son-license-error]'), error.message);
    } finally {
        setBusy(false);
    }
}

function toggle(button) {
    const block = button.closest('[data-son-license]');
    const open = !block.classList.contains('is-showing-inactive');
    block.classList.toggle('is-showing-inactive', open);
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    button.querySelector('span').textContent = open ? button.dataset.hide : button.dataset.show;
}

function openIlok(button) {
    const dialog = document.querySelector('[data-son-licenses-ilok]');
    const form = dialog?.querySelector('form');
    if (!form) {
        return;
    }
    form.reset();
    form.elements.key.value = button.closest('[data-son-license]').dataset.sonLicense;
    showError(dialog.querySelector('[data-son-licenses-ilok-error]'), '');
    dialog.showModal();
}

async function submitIlok(form) {
    const dialog = form.closest('dialog');
    const submit = form.querySelector('[type=submit]');
    const errorElement = dialog.querySelector('[data-son-licenses-ilok-error]');
    const account = form.elements.account.value.trim();
    const confirm = form.elements.account_confirm.value.trim();

    if (!account || account !== confirm) {
        showError(errorElement, form.dataset[account ? 'mismatch' : 'empty'] || config().error);
        return;
    }
    if (busy) {
        return;
    }
    setBusy(true);
    submit.disabled = true;
    showError(errorElement, '');

    try {
        const key = form.elements.key.value;
        const result = await post('ilok', { key, account, account_confirm: confirm });
        dialog.close();
        replace(key, result.html);
        popup(result.popup).then(() => focusLicense(key));
    } catch (error) {
        if (dialog.open) {
            showError(errorElement, error.message);
        } else {
            infoDialog({ text: error.message, button: config().button }); // closed while the call ran
        }
    } finally {
        setBusy(false);
        submit.disabled = false;
    }
}

document.addEventListener('click', (event) => {
    if (!config()) {
        return;
    }

    const action = event.target.closest('[data-son-license-action]');
    const info = event.target.closest('[data-son-license-popup]');
    const toggleButton = event.target.closest('[data-son-license-toggle]');
    const ilok = event.target.closest('[data-son-license-ilok]');
    const close = event.target.closest('.son-licenses-ilok__close');

    if (action) {
        act(action);
    } else if (info) {
        popup(info.dataset.sonLicensePopup);
    } else if (toggleButton) {
        toggle(toggleButton);
    } else if (ilok) {
        openIlok(ilok);
    } else if (close) {
        close.closest('dialog').close();
    }
});

// The new key's order changes the whole list, so a success reloads the page.
async function submitRegister(form) {
    const message = form.querySelector('[data-son-licenses-register-message]');
    if (busy) {
        return;
    }
    setBusy(true);
    message.hidden = true;
    message.classList.remove('is-error');

    try {
        const result = await post('register', { key: form.elements.key.value.trim() });
        message.textContent = result.message;
        message.hidden = false;
        window.location.reload();
    } catch (error) {
        message.innerHTML = error.message; // ours, may carry the support link
        message.classList.add('is-error');
        message.hidden = false;
        setBusy(false); // a success keeps everything disabled until the reload
    }
}

document.addEventListener('submit', (event) => {
    if (!config()) {
        return;
    }
    const ilok = event.target.closest('.son-licenses-ilok__form');
    const register = event.target.closest('[data-son-licenses-register]');
    if (ilok) {
        event.preventDefault();
        submitIlok(ilok);
    } else if (register) {
        event.preventDefault();
        submitRegister(register);
    }
});
